import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useRecordContext,
  useInput,
} from 'react-admin';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import type { Variant } from '@src/types';
import { ImageUpload } from '../shared';

const ALL_VALUE = '__all__';

/** Derive a dropdown label from a key like "seasonDisplays" → "Season". */
const toLabel = (key: string): string => {
  const stripped = key.replace(/Displays$/i, '');
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

/** Find the next available image name when a name collision occurs. */
const nextAvailableName = (name: string, taken: Record<string, string>): string => {
  if (!(name in taken)) return name;
  const match = name.match(/^(.*?)(\d+)$/);
  const base = match ? match[1] : name;
  let num = match ? parseInt(match[2], 10) + 1 : 2;
  while (`${base}${num}` in taken) num++;
  return `${base}${num}`;
};

/**
 * Merge images from multiple scope keys, deduplicating by URL.
 * If two scope keys have different URLs under the same name (e.g. both have "alt1"),
 * the second gets renamed (e.g. "alt2") so all unique images are visible.
 */
const mergeImages = (
  scopeKeys: string[],
  imageMap: Record<string, Record<string, string>> | undefined,
): Record<string, string> => {
  if (scopeKeys.length === 0 || !imageMap) return {};
  const merged: Record<string, string> = {};
  const seenUrls = new Set<string>();

  for (const key of scopeKeys) {
    const images = imageMap[key];
    if (!images) continue;
    for (const [name, url] of Object.entries(images)) {
      const urlStr = url as string;
      if (seenUrls.has(urlStr)) continue;
      seenUrls.add(urlStr);
      merged[nextAvailableName(name, merged)] = urlStr;
    }
  }
  return merged;
};

/** A filterable dimension derived from variant data (size, season, etc.). */
interface FilterDimension {
  key: string;
  label: string;
  /** Parallel array to variant.skus — value at index i is for skus[i]. */
  allValues: string[];
}

const ImagesSection = () => {
  const record = useRecordContext();
  const variants: Variant[] = record?.variants ?? [];

  // Form fields for partner images
  const { field: partnerImagesField } = useInput({ source: 'imagesByColorway' });
  const { field: partnerFilesField } = useInput({ source: 'files' });
  const { field: partnerDirtyField } = useInput({ source: 'partnerImagesDirty', defaultValue: false });

  // Form fields for studio images
  const { field: studioImagesField } = useInput({ source: 'studioImagesByColorway' });
  const { field: studioFilesField } = useInput({ source: 'studioFiles' });
  const { field: studioDirtyField } = useInput({ source: 'studioImagesDirty', defaultValue: false });

  // Dropdown state
  const [selectedColorway, setSelectedColorway] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});

  // Colorway options: derived from variants, alphabetized
  const colorwayOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { code: string; display: string }[] = [];
    for (const v of variants) {
      if (!seen.has(v.colorCode)) {
        seen.add(v.colorCode);
        options.push({ code: v.colorCode, display: v.colorDisplay });
      }
    }
    return options.sort((a, b) => a.display.localeCompare(b.display));
  }, [variants]);

  // Auto-select first colorway on mount or when options change
  useEffect(() => {
    if (colorwayOptions.length > 0 && !colorwayOptions.find((o) => o.code === selectedColorway)) {
      setSelectedColorway(colorwayOptions[0].code);
    }
  }, [colorwayOptions, selectedColorway]);

  // The variant matching the selected colorway
  const selectedVariant = useMemo(
    () => variants.find((v) => v.colorCode === selectedColorway) ?? null,
    [variants, selectedColorway],
  );

  // Build the list of filterable dimensions from variant data
  const filterDimensions: FilterDimension[] = useMemo(() => {
    if (!selectedVariant) return [];
    const dims: FilterDimension[] = [
      { key: 'sizeDisplays', label: 'Size', allValues: selectedVariant.sizeDisplays },
    ];
    if (selectedVariant.otherVitalElements) {
      for (const [key, values] of Object.entries(selectedVariant.otherVitalElements)) {
        dims.push({ key, label: toLabel(key), allValues: values });
      }
    }
    return dims;
  }, [selectedVariant]);

  // Compute the available (deduplicated, sorted) values for a given dimension,
  // considering the selected values of all OTHER dimensions.
  const getAvailableValues = useCallback(
    (dimKey: string): string[] => {
      if (!selectedVariant) return [];

      // Start with all SKU indices for this variant
      let indices = selectedVariant.skus.map((_, i) => i);

      // Filter by other dimensions' selections
      for (const dim of filterDimensions) {
        if (dim.key === dimKey) continue;
        const filterVal = selectedFilters[dim.key];
        if (!filterVal || filterVal === ALL_VALUE) continue;
        indices = indices.filter((i) => dim.allValues[i] === filterVal);
      }

      // Deduplicate values at the remaining indices
      const targetDim = filterDimensions.find((d) => d.key === dimKey);
      if (!targetDim) return [];

      const seen = new Set<string>();
      const result: string[] = [];
      for (const i of indices) {
        const val = targetDim.allValues[i];
        if (val && !seen.has(val)) {
          seen.add(val);
          result.push(val);
        }
      }
      return result.sort((a, b) => a.localeCompare(b));
    },
    [selectedVariant, filterDimensions, selectedFilters],
  );

  // All SKUs that match the current filter combination.
  // Empty array means no optional filters are active (colorway level).
  const matchingSkus = useMemo(() => {
    if (!selectedVariant) return [];

    const hasAnyFilter = Object.entries(selectedFilters).some(
      ([, v]) => v && v !== ALL_VALUE,
    );
    if (!hasAnyFilter) return [];

    let indices = selectedVariant.skus.map((_, i) => i);
    for (const dim of filterDimensions) {
      const val = selectedFilters[dim.key];
      if (!val || val === ALL_VALUE) continue;
      indices = indices.filter((i) => dim.allValues[i] === val);
    }

    return indices.map((i) => selectedVariant.skus[i]);
  }, [selectedVariant, selectedFilters, filterDimensions]);

  // Scope keys — determines both what images are displayed and where saves go.
  // Builds composite keys from SKU parts, using ??? as wildcard for unselected filters.
  // Format: partnerCode_parentSku_color_size_<otherVitalElements...>
  // Examples:
  //   - Default partner (color+size): WW_43692_BLK_??? (color-level)
  //   - Patagonia (color+size+season): WW_43692_BLK_???_??? (color-level)
  //   - Patagonia with size filter: WW_43692_BLK_XXS_??? (color+size-level)
  const scopeKeys = useMemo(() => {
    if (!selectedColorway || !selectedVariant || selectedVariant.skus.length === 0) return [];

    // Parse any SKU to determine the structure
    const firstSku = selectedVariant.skus[0];
    const skuParts = firstSku.split('_');

    if (skuParts.length < 4) {
      // Invalid SKU format, fall back to colorway
      return [selectedColorway];
    }

    // Base key is always partnerCode_parentSku_color (first 3 parts)
    const baseKey = skuParts.slice(0, 3).join('_');

    // If no filters are active, build color-level key with wildcards
    // Use filterDimensions to determine how many vital elements exist
    if (matchingSkus.length === 0) {
      // Fill remaining positions with ??? based on number of vital elements
      const wildcards = Array(filterDimensions.length).fill('???');
      return [[baseKey, ...wildcards].join('_')];
    }

    // Build key based on selected filter values
    // Start with the base key parts (partnerCode_parentSku_color)
    const keyParts = [...skuParts.slice(0, 3)];

    // For each dimension (size, then other vital elements), add the code value or ???
    for (let i = 0; i < filterDimensions.length; i++) {
      const dim = filterDimensions[i];
      const filterValue = selectedFilters[dim.key];

      if (filterValue && filterValue !== ALL_VALUE) {
        // Specific value selected - map display value to code value
        // Find a SKU index that has this display value for this dimension
        const skuIndex = dim.allValues.findIndex(val => val === filterValue);
        if (skuIndex !== -1 && selectedVariant.skus[skuIndex]) {
          // Extract the code from the SKU at this position
          const skuParts = selectedVariant.skus[skuIndex].split('_');
          const codeValue = skuParts[3 + i]; // Position after base parts (partnerCode_parentSku_color)
          keyParts.push(codeValue);
        } else {
          // Fallback to display value if we can't find the code
          keyParts.push(filterValue);
        }
      } else {
        // "All" selected - use wildcard
        keyParts.push('???');
      }
    }

    return [keyParts.join('_')];
  }, [selectedColorway, selectedVariant, matchingSkus, selectedFilters, filterDimensions]);

  // Scope keys for reading - determines what images are displayed
  // - At color level: includes ALL existing scope keys for this colorway
  // - With filters: includes only the filtered scope keys + matching SKUs
  const scopeKeysForReading = useMemo(() => {
    if (!selectedVariant || selectedVariant.skus.length === 0) return scopeKeys;

    const keys = new Set<string>(scopeKeys);

    if (matchingSkus.length > 0) {
      // Filters are active - only include matching SKU keys
      matchingSkus.forEach(sku => keys.add(sku));
    } else {
      // At color level - include ALL existing scope keys for this colorway
      const colorPrefix = scopeKeys[0]?.split('_').slice(0, 3).join('_') + '_';
      const existingPartnerKeys = Object.keys(record?.imagesByColorway || {});
      const existingStudioKeys = Object.keys(record?.studioImagesByColorway || {});

      for (const key of [...existingPartnerKeys, ...existingStudioKeys]) {
        if (key.startsWith(colorPrefix)) {
          keys.add(key);
        }
      }
    }

    return Array.from(keys);
  }, [scopeKeys, matchingSkus, selectedVariant, record?.imagesByColorway, record?.studioImagesByColorway]);

  // Merge images from all scope keys, deduplicating by URL and renaming collisions.
  const partnerImages: Record<string, string> = useMemo(
    () => mergeImages(scopeKeysForReading, record?.imagesByColorway),
    [scopeKeysForReading, record?.imagesByColorway],
  );

  const studioImages: Record<string, string> = useMemo(() => {
    const merged = mergeImages(scopeKeysForReading, record?.studioImagesByColorway);
    // Normalize studio image keys that conflict with partner images
    const normalized: Record<string, string> = {};
    for (const [key, url] of Object.entries(merged)) {
      if (key in partnerImages && !key.startsWith('studio_')) {
        // Conflict detected - add studio_ prefix
        normalized[`studio_${key}`] = url;
      } else {
        normalized[key] = url;
      }
    }
    return normalized;
  }, [scopeKeysForReading, record?.studioImagesByColorway, partnerImages]);

  // Apply changes for immediate UI feedback (all visible keys) and backend save (intended keys only).
  // - Local record updates all visible keys for immediate UI feedback
  // - Form field updates only intended scope keys for backend
  // - Backend handles cascading logic and returns correct state after save
  const handlePartnerImagesChange = useCallback((
    updatedImages: Record<string, string>,
    files: Record<string, File>,
  ) => {
    if (scopeKeys.length === 0 || !record || !selectedVariant) return;

    // Merge updates into existing data to preserve other colorways/scopes
    const existingImagesByScope = { ...(record.imagesByColorway || {}) } as Record<string, Record<string, string>>;
    const updatedImagesByScope: Record<string, Record<string, string>> = {
      ...existingImagesByScope,
    };

    // Apply updates to all visible scope keys (scopeKeysForReading) for immediate UI feedback.
    // This ensures deletions are immediately reflected in the UI.
    for (const scopeKey of scopeKeysForReading) {
      updatedImagesByScope[scopeKey] = { ...updatedImages };
    }

    // For backend: create a separate object with only the intended write scope keys.
    // This prevents sending unintended SKU-level updates when editing at a broader scope.
    const scopeKeysForBackend: Record<string, Record<string, string>> = {};
    for (const scopeKey of scopeKeys) {
      scopeKeysForBackend[scopeKey] = { ...updatedImages };
    }

    // Clear old file entries for all scope keys, but only upload new files
    // under the first scope key to avoid duplicate uploads. The data provider
    // will propagate the resulting Cloudinary URL to all scope keys.
    const updatedFiles = { ...((record.files || {}) as Record<string, File>) };
    for (const scopeKey of scopeKeys) {
      for (const key of Object.keys(updatedFiles)) {
        if (key.startsWith(`${scopeKey}_`)) delete updatedFiles[key];
      }
    }
    Object.entries(files).forEach(([key, file]) => {
      updatedFiles[`${scopeKeys[0]}_${key}`] = file;
    });

    // Update local state with all visible keys for UI feedback
    record.imagesByColorway = updatedImagesByScope;
    record.files = updatedFiles;

    // Send only intended scope keys to backend
    partnerImagesField.onChange(scopeKeysForBackend);
    partnerFilesField.onChange(updatedFiles);
    partnerDirtyField.onChange(true);
  }, [scopeKeys, scopeKeysForReading, selectedVariant, record, partnerImagesField, partnerFilesField, partnerDirtyField]);

  const handleStudioImagesChange = useCallback((
    updatedImages: Record<string, string>,
    files: Record<string, File>,
  ) => {
    if (scopeKeys.length === 0 || !record || !selectedVariant) return;

    // Validate: ensure no keys conflict with partnerImages
    const conflictingKeys = Object.keys(updatedImages).filter(key => key in partnerImages);
    if (conflictingKeys.length > 0) {
      alert(`Cannot save: Studio image keys conflict with partner image keys: ${conflictingKeys.join(', ')}`);
      return;
    }

    // Merge updates into existing data to preserve other colorways/scopes
    const existingImagesByScope = { ...(record.studioImagesByColorway || {}) } as Record<string, Record<string, string>>;
    const updatedImagesByScope: Record<string, Record<string, string>> = {
      ...existingImagesByScope,
    };

    // Apply updates to all visible scope keys (scopeKeysForReading) for immediate UI feedback.
    // This ensures deletions are immediately reflected in the UI.
    for (const scopeKey of scopeKeysForReading) {
      updatedImagesByScope[scopeKey] = { ...updatedImages };
    }

    // For backend: create a separate object with only the intended write scope keys.
    // This prevents sending unintended SKU-level updates when editing at a broader scope.
    const scopeKeysForBackend: Record<string, Record<string, string>> = {};
    for (const scopeKey of scopeKeys) {
      scopeKeysForBackend[scopeKey] = { ...updatedImages };
    }

    const updatedFiles = { ...((record.studioFiles || {}) as Record<string, File>) };
    for (const scopeKey of scopeKeys) {
      for (const key of Object.keys(updatedFiles)) {
        if (key.startsWith(`${scopeKey}_`)) delete updatedFiles[key];
      }
    }
    Object.entries(files).forEach(([key, file]) => {
      updatedFiles[`${scopeKeys[0]}_${key}`] = file;
    });

    // Update local state with all visible keys for UI feedback
    record.studioImagesByColorway = updatedImagesByScope;
    record.studioFiles = updatedFiles;

    // Send only intended scope keys to backend
    studioImagesField.onChange(scopeKeysForBackend);
    studioFilesField.onChange(updatedFiles);
    studioDirtyField.onChange(true);
  }, [scopeKeys, scopeKeysForReading, selectedVariant, record, studioImagesField, studioFilesField, studioDirtyField, partnerImages]);

  if (!record?.variants || !Array.isArray(record.variants)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No variants available for image management.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Dropdown Filters Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Colorway</InputLabel>
          <Select
            value={selectedColorway}
            onChange={(e) => {
              setSelectedColorway(e.target.value);
              setSelectedFilters({});
            }}
            label="Colorway"
          >
            {colorwayOptions.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>{opt.display}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {filterDimensions.map((dim) => {
          const availableValues = getAvailableValues(dim.key);
          const currentValue = selectedFilters[dim.key] ?? ALL_VALUE;
          return (
            <FormControl key={dim.key} size="small" sx={{ minWidth: 200 }}>
              <InputLabel>
                {dim.label}
                {' '}
                (optional)
              </InputLabel>
              <Select
                value={currentValue}
                onChange={(e) => setSelectedFilters((prev) => ({
                  ...prev,
                  [dim.key]: e.target.value,
                }))}
                label={`${dim.label} (optional)`}
              >
                <MenuItem value={ALL_VALUE}>
                  All
                  {' '}
                  {dim.label.toLowerCase()}
                  s
                </MenuItem>
                {availableValues.map((val) => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
      </Box>

      {/* Partner Catalog Images */}
      <Typography variant="h6" gutterBottom>
        Partner Catalog Images
      </Typography>
      <ImageUpload
        images={partnerImages}
        onImagesChange={handlePartnerImagesChange}
        maxFileSize={10}
        allowRename={true}
      />

      <Divider sx={{ my: 3 }} />

      {/* Promoted Studio Images */}
      <Typography variant="h6" gutterBottom>
        Promoted Studio Images
      </Typography>
      <ImageUpload
        images={studioImages}
        onImagesChange={handleStudioImagesChange}
        maxFileSize={10}
        allowRename={true}
        keyPrefix="studio_"
        reservedKeys={partnerImages}
      />
    </Box>
  );
};

export default ImagesSection;
